<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div class="rbz-skeleton-wrapper">
    <!-- Skeleton -->
    <div v-if="loading" class="skeleton-container">
      <!-- Stat Card Skeleton -->
      <template v-if="type === 'stat-cards'">
        <div class="stat-cards-skeleton">
          <div v-for="n in count" :key="n" class="stat-card-skeleton">
            <div class="skeleton-line skeleton-title"></div>
            <div class="skeleton-line skeleton-value"></div>
          </div>
        </div>
      </template>

      <!-- Area Chart Skeleton -->
      <template v-else-if="type === 'chart'">
        <div class="chart-skeleton" :style="{ height: `${height}px` }">
          <!-- Y-axis -->
          <div class="chart-y-axis">
            <div class="y-axis-label skeleton-text"></div>
            <div class="y-axis-label skeleton-text"></div>
            <div class="y-axis-label skeleton-text"></div>
          </div>
          <!-- Chart area -->
          <div class="chart-main-area">
            <svg class="chart-skeleton-svg" viewBox="0 0 100 50" preserveAspectRatio="none">
              <defs>
                <linearGradient id="shimmerGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stop-color="transparent" />
                  <stop offset="50%" stop-color="rgba(255, 255, 255, 0.5)" />
                  <stop offset="100%" stop-color="transparent" />
                  <animate
                    attributeName="x1"
                    from="-100%"
                    to="100%"
                    dur="1.5s"
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="x2"
                    from="0%"
                    to="200%"
                    dur="1.5s"
                    repeatCount="indefinite"
                  />
                </linearGradient>
                <mask id="areaMask">
                  <path :d="areaPath" fill="white" />
                </mask>
              </defs>
              <!-- Horizontal grid lines -->
              <line x1="0" y1="10" x2="100" y2="10" class="chart-grid-line" />
              <line x1="0" y1="25" x2="100" y2="25" class="chart-grid-line" />
              <line x1="0" y1="40" x2="100" y2="40" class="chart-grid-line" />
              <!-- Base area -->
              <path :d="areaPath" class="chart-area-base" />
              <!-- Shimmer overlay -->
              <rect
                x="0"
                y="0"
                width="100"
                height="50"
                fill="url(#shimmerGradient)"
                mask="url(#areaMask)"
              />
              <!-- Line on top -->
              <path :d="linePath" class="chart-line" />
            </svg>
            <!-- X-axis -->
            <div class="chart-x-axis">
              <div class="x-axis-label skeleton-text" v-for="n in 12" :key="n"></div>
            </div>
          </div>
        </div>
      </template>

      <!-- Table Skeleton -->
      <template v-else-if="type === 'table'">
        <div class="table-skeleton">
          <div class="skeleton-header">
            <div class="skeleton-cell" v-for="n in columns" :key="n"></div>
          </div>
          <div class="skeleton-row" v-for="n in rows" :key="n">
            <div class="skeleton-cell" v-for="m in columns" :key="m"></div>
          </div>
        </div>
      </template>
    </div>

    <div v-else :class="{ 'is-refreshing': refreshing }">
      <slot></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface Props {
  loading?: boolean;
  refreshing?: boolean;
  type?: 'stat-cards' | 'chart' | 'table';
  count?: number;
  height?: number;
  rows?: number;
  columns?: number;
}

withDefaults(defineProps<Props>(), {
  loading: false,
  refreshing: false,
  type: 'table',
  count: 4,
  height: 370,
  rows: 5,
  columns: 8,
});

// Generate smooth bezier curve for area chart skeleton
const chartPoints = [
  { x: 0, y: 35 },
  { x: 15, y: 25 },
  { x: 30, y: 32 },
  { x: 45, y: 18 },
  { x: 60, y: 22 },
  { x: 75, y: 12 },
  { x: 90, y: 20 },
  { x: 100, y: 15 },
];

// Create smooth bezier curve path
const createSmoothPath = (points: { x: number; y: number }[]) => {
  if (points.length < 2) return '';

  let path = `M${points[0].x},${points[0].y}`;

  for (let i = 0; i < points.length - 1; i++) {
    const current = points[i];
    const next = points[i + 1];

    // Control points for smooth curve
    const cpx1 = current.x + (next.x - current.x) / 3;
    const cpy1 = current.y;
    const cpx2 = next.x - (next.x - current.x) / 3;
    const cpy2 = next.y;

    path += ` C${cpx1},${cpy1} ${cpx2},${cpy2} ${next.x},${next.y}`;
  }

  return path;
};

const linePath = computed(() => createSmoothPath(chartPoints));

const areaPath = computed(() => {
  return `${linePath.value} L100,50 L0,50 Z`;
});
</script>

<style scoped lang="scss">
@use 'sass:map';
@use 'ui-component-library/assets/styles/colors' as colors;

// Shimmer animation
@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }

  100% {
    transform: translateX(100%);
  }
}

.is-refreshing {
  opacity: 0.6;
  pointer-events: none;
  transition: opacity 0.2s ease;
}

.skeleton-line,
.skeleton-cell,
.skeleton-text {
  background: map.get(colors.$colors, 'gallery');
  border-radius: 4px;
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.5) 50%,
    transparent 100%);
    animation: shimmer 1.5s infinite;
  }
}

.stat-cards-skeleton {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;

  @media (width <= 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.stat-card-skeleton {
  background: map.get(colors.$colors, 'white');
  border-radius: 8px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 135px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.skeleton-title {
  height: 14px;
  width: 60%;
}

.skeleton-value {
  height: 32px;
  width: 40%;
}

// Area chart skeleton
.chart-skeleton {
  display: flex;
  flex-direction: row;
  box-sizing: border-box;
  padding: 0 42px;
  gap: 8px;
}

.chart-y-axis {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 32px 0 14px;
  width: 24px;
}

.y-axis-label {
  width: 16px;
  height: 10px;
}

.chart-main-area {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.chart-skeleton-svg {
  height: 370px;
  width: 100%;
}

.chart-grid-line {
  stroke: map.get(colors.$colors, 'gallery');
  stroke-width: 0.3;
}

.chart-x-axis {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 8px;
}

.x-axis-label {
  width: 40px;
  height: 10px;
  margin-bottom: 30px;
}

.chart-area-base {
  fill: map.get(colors.$colors, 'gallery');
}

.chart-line {
  fill: none;
  stroke: map.get(colors.$colors, 'shuttle-gray');
  stroke-width: 0.5;
  opacity: 0.5;
}

.table-skeleton {
  padding: 16px;
}

.skeleton-header,
.skeleton-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr 1fr 100px;
  gap: 16px;
  padding: 12px 0;
}

.skeleton-header {
  border-bottom: 1px solid map.get(colors.$colors, 'gallery');
}

.skeleton-cell {
  height: 20px;
}
</style>
